import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PayLine, Payslip } from '@/client/types/hr';
import { money } from './comp-format';

const FLAG_TEXT: Record<string, string> = {
  no_tax_rules: 'No tax rules for this currency: no PAYE or SSNIT was worked out. Handle the statutory side by hand.',
};

const monthName = (period?: string) =>
  period ? new Date(`${period}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';

function Section({ title, lines, currency, negative = false }: { title: string; lines: PayLine[]; currency: string; negative?: boolean }) {
  if (!lines.length) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">{title}</p>
      <table className="w-full text-sm">
        <tbody>
          {lines.map((l, i) => (
            <tr key={`${l.code}${i}`}>
              <td className="py-1 text-gray-700">
                {l.label}
                {l.note && <span className="ml-1 text-xs text-gray-400">({l.note})</span>}
              </td>
              <td className="py-1 text-right text-gray-900">{money(negative ? -l.amount : l.amount, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

/** Opens a plain, printable payslip in its own window (save as PDF from there). */
export function printPayslip(slip: Payslip, name: string, period?: string) {
  const rows = (lines: PayLine[], sign = 1) =>
    lines.map((l) => `<tr><td>${esc(l.label)}${l.note ? ` <small>(${esc(l.note)})</small>` : ''}</td><td class="r">${esc(money(sign * l.amount, slip.currency))}</td></tr>`).join('');
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Payslip ${esc(name)} ${esc(period ?? '')}</title>
<style>body{font:14px system-ui,sans-serif;color:#111;margin:40px}h1{font-size:20px;margin:0}small{color:#666}
table{width:100%;border-collapse:collapse;margin:8px 0 20px}td{padding:4px 0;border-bottom:1px solid #eee}.r{text-align:right}
h2{font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#666;margin:16px 0 0}.net{font-size:18px;font-weight:600}</style></head>
<body><h1>FeelyFeely: payslip</h1><p>${esc(name)}<br><small>${esc(monthName(period))}${slip.payDate ? ` · paid ${esc(slip.payDate)}` : ''}</small></p>
<h2>Earnings</h2><table>${rows(slip.lines.earnings)}<tr><td><b>Gross</b></td><td class="r"><b>${esc(money(slip.gross, slip.currency))}</b></td></tr></table>
<h2>Deductions</h2><table>${rows(slip.lines.deductions, -1)}<tr><td><b>Total deductions</b></td><td class="r"><b>${esc(money(-slip.deductions, slip.currency))}</b></td></tr></table>
<table><tr><td class="net">Net pay</td><td class="r net">${esc(money(slip.net, slip.currency))}</td></tr></table>
${slip.lines.employer.length ? `<h2>Paid by the employer on top</h2><table>${rows(slip.lines.employer)}</table>` : ''}
</body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

/** A payslip, as the employee and payroll see it. */
export function PayslipView({ slip, name, period }: { slip: Payslip; name: string; period?: string }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-gray-900">{name}</p>
          <p className="text-xs text-gray-500">{monthName(period ?? slip.period)}</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => printPayslip(slip, name, period ?? slip.period)}>
          <Printer className="h-3.5 w-3.5" /> Print / PDF
        </Button>
      </div>
      {slip.flags.map((f) => (
        <p key={f} className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {FLAG_TEXT[f] ?? f}
        </p>
      ))}
      <Section title="Earnings" lines={slip.lines.earnings} currency={slip.currency} />
      <div className="flex justify-between border-t border-gray-100 pt-2 text-sm">
        <span className="text-gray-600">Gross</span>
        <span className="font-medium text-gray-900">{money(slip.gross, slip.currency)}</span>
      </div>
      <Section title="Deductions" lines={slip.lines.deductions} currency={slip.currency} negative />
      <div className="flex justify-between rounded-lg bg-gray-50 px-3 py-2">
        <span className="font-medium text-gray-900">Net pay</span>
        <span className="text-lg font-semibold text-gray-900">{money(slip.net, slip.currency)}</span>
      </div>
      <Section title="Paid by the employer on top" lines={slip.lines.employer} currency={slip.currency} />
    </div>
  );
}
